import { useState, useRef, useCallback, useEffect } from 'react';
import {
  ConnectionState,
  Message,
  WebSocketOptions,
  UseWebSocketReturn,
} from '../types/Collaboration';

/**
 * Default WebSocket options
 */
const DEFAULT_OPTIONS: Required<WebSocketOptions> = {
  url: import.meta.env.VITE_WS_SERVER_URL || 'ws://localhost:3001',
  autoConnect: false,
  reconnect: true,
  maxReconnectAttempts: 5,
  reconnectInterval: 1000, // Initial interval, will use exponential backoff
  pingInterval: 30000, // 30 seconds
};

/**
 * Maximum size of the message queue
 */
const MAX_QUEUE_SIZE = 100;

/**
 * Maximum age for queued messages (5 minutes)
 */
const MAX_MESSAGE_AGE = 5 * 60 * 1000;

/**
 * Queued message with metadata
 */
interface QueuedMessage {
  message: Message;
  queuedAt: number;
  retryCount: number;
}

/**
 * Network status
 */
export interface NetworkStatus {
  isOnline: boolean;
  lastOnlineAt: number | null;
  lastOfflineAt: number | null;
  pendingMessages: number;
}

/**
 * Extended return type with network status
 */
export interface UseWebSocketReturnExtended extends UseWebSocketReturn {
  networkStatus: NetworkStatus;
  getPendingMessagesCount: () => number;
  clearPendingMessages: () => void;
  retryPendingMessages: () => void;
}

/**
 * WebSocket client hook with reconnection and heartbeat support
 */
export function useWebSocket(options: Partial<WebSocketOptions> = {}): UseWebSocketReturnExtended {
  const config = { ...DEFAULT_OPTIONS, ...options };

  // State
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastMessage, setLastMessage] = useState<Message | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Message queue to prevent losing messages during React batching
  const incomingMessageQueueRef = useRef<Message[]>([]);
  const isProcessingQueueRef = useRef<boolean>(false);
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    lastOnlineAt: null,
    lastOfflineAt: null,
    pendingMessages: 0,
  });

  // Refs for persistent values
  const wsRef = useRef<WebSocket | null>(null);
  const activeUrlRef = useRef<string | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const messageQueueRef = useRef<QueuedMessage[]>([]);
  const isManualDisconnectRef = useRef(false);
  const lastSyncTimeRef = useRef<number>(Date.now());

  /**
   * Process incoming message queue one at a time
   * This ensures messages are processed in order without being lost to React batching
   */
  const processIncomingQueue = useCallback(() => {
    if (isProcessingQueueRef.current) return;
    if (incomingMessageQueueRef.current.length === 0) return;

    isProcessingQueueRef.current = true;
    const message = incomingMessageQueueRef.current.shift()!;

    // Use requestAnimationFrame to ensure React processes each message separately
    setLastMessage(message);

    // Schedule next message processing
    requestAnimationFrame(() => {
      isProcessingQueueRef.current = false;
      if (incomingMessageQueueRef.current.length > 0) {
        processIncomingQueue();
      }
    });
  }, []);

  /**
   * Clear all timers
   */
  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  /**
   * Start heartbeat ping
   */
  const startHeartbeat = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        const pingMessage: Message = {
          type: 'ping',
          timestamp: Date.now(),
        };
        wsRef.current.send(JSON.stringify(pingMessage));
      }
    }, config.pingInterval);
  }, [config.pingInterval]);

  /**
   * Update network status
   */
  const updateNetworkStatus = useCallback((updates: Partial<NetworkStatus>) => {
    setNetworkStatus((prev) => ({
      ...prev,
      ...updates,
      pendingMessages: messageQueueRef.current.length,
    }));
  }, []);

  /**
   * Clean stale messages from queue
   */
  const cleanStaleMessages = useCallback(() => {
    const now = Date.now();
    const before = messageQueueRef.current.length;

    messageQueueRef.current = messageQueueRef.current.filter(
      (qm) => now - qm.queuedAt < MAX_MESSAGE_AGE
    );

    const cleaned = before - messageQueueRef.current.length;
    if (cleaned > 0) {
      console.log(`[WebSocket] Cleaned ${cleaned} stale messages from queue`);
      updateNetworkStatus({ pendingMessages: messageQueueRef.current.length });
    }
  }, [updateNetworkStatus]);

  /**
   * Process queued messages
   */
  const processMessageQueue = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      // Clean stale messages first
      cleanStaleMessages();

      const now = Date.now();
      const processed: QueuedMessage[] = [];

      while (messageQueueRef.current.length > 0) {
        const qm = messageQueueRef.current.shift();
        if (qm) {
          try {
            wsRef.current.send(JSON.stringify(qm.message));
            processed.push(qm);
          } catch (err) {
            console.error('[WebSocket] Failed to send queued message:', err);
            // Put it back with incremented retry count
            if (qm.retryCount < 3) {
              messageQueueRef.current.unshift({
                ...qm,
                retryCount: qm.retryCount + 1,
              });
            }
            break; // Stop processing on error
          }
        }
      }

      if (processed.length > 0) {
        console.log(`[WebSocket] Processed ${processed.length} queued messages`);
        lastSyncTimeRef.current = now;
        updateNetworkStatus({ pendingMessages: messageQueueRef.current.length });
      }
    }
  }, [cleanStaleMessages, updateNetworkStatus]);

  /**
   * Schedule reconnection with exponential backoff
   */
  const scheduleReconnect = useCallback(() => {
    if (!config.reconnect || isManualDisconnectRef.current) {
      return;
    }

    if (reconnectAttemptsRef.current >= config.maxReconnectAttempts) {
      setConnectionState('error');
      setError(`Failed to connect after ${config.maxReconnectAttempts} attempts`);
      return;
    }

    setConnectionState('reconnecting');

    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, ...
    const delay = config.reconnectInterval * Math.pow(2, reconnectAttemptsRef.current);
    reconnectAttemptsRef.current += 1;

    reconnectTimeoutRef.current = setTimeout(() => {
      connect();
    }, delay);
  }, [config.reconnect, config.maxReconnectAttempts, config.reconnectInterval]);

  /**
   * Connect to WebSocket server
   */
  const connect = useCallback((urlOverride?: string) => {
    // Store URL override for reconnection
    if (urlOverride) {
      activeUrlRef.current = urlOverride;
    }
    const wsUrl = activeUrlRef.current || config.url;

    // Prevent duplicate connections (important for React.StrictMode)
    if (wsRef.current) {
      const readyState = wsRef.current.readyState;
      if (readyState === WebSocket.CONNECTING || readyState === WebSocket.OPEN) {
        console.log('[WebSocket] Already connected or connecting, skipping');
        return;
      }
      wsRef.current.close();
      wsRef.current = null;
    }

    clearTimers();
    isManualDisconnectRef.current = false;
    setConnectionState('connecting');
    setError(null);

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;
        startHeartbeat();
        processMessageQueue();
      };

      ws.onclose = () => {
        clearTimers();
        wsRef.current = null;

        if (!isManualDisconnectRef.current) {
          // Unexpected close, try to reconnect
          scheduleReconnect();
        } else {
          setConnectionState('disconnected');
        }
      };

      ws.onerror = (event) => {
        console.error('WebSocket error:', event);
        setError('WebSocket connection error');
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as Message;

          // Handle pong internally
          if (message.type === 'pong') {
            return;
          }

          // Handle error messages
          if (message.type === 'error') {
            setError((message as any).error);
          }

          // Queue message and process to prevent React batching from losing messages
          incomingMessageQueueRef.current.push(message);
          processIncomingQueue();
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Failed to create WebSocket:', err);
      setError('Failed to create WebSocket connection');
      setConnectionState('error');
    }
  }, [config.url, clearTimers, startHeartbeat, processMessageQueue, scheduleReconnect, processIncomingQueue]);

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    isManualDisconnectRef.current = true;
    clearTimers();
    reconnectAttemptsRef.current = 0;
    messageQueueRef.current = [];

    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }

    setConnectionState('disconnected');
    setError(null);
  }, [clearTimers]);

  /**
   * Send a message to the server
   */
  const send = useCallback((message: Message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
        lastSyncTimeRef.current = Date.now();
      } catch (err) {
        console.error('[WebSocket] Failed to send message, queueing:', err);
        // Queue message for when connection is restored
        if (messageQueueRef.current.length < MAX_QUEUE_SIZE) {
          messageQueueRef.current.push({
            message,
            queuedAt: Date.now(),
            retryCount: 0,
          });
          updateNetworkStatus({ pendingMessages: messageQueueRef.current.length });
        } else {
          console.warn('[WebSocket] Message queue full, dropping message');
        }
      }
    } else {
      // Queue message for when connection is restored
      if (messageQueueRef.current.length < MAX_QUEUE_SIZE) {
        messageQueueRef.current.push({
          message,
          queuedAt: Date.now(),
          retryCount: 0,
        });
        updateNetworkStatus({ pendingMessages: messageQueueRef.current.length });
        console.log(`[WebSocket] Message queued (${messageQueueRef.current.length} pending)`);
      } else {
        console.warn('[WebSocket] Message queue full, dropping message');
      }
    }
  }, [updateNetworkStatus]);

  /**
   * Get pending messages count
   */
  const getPendingMessagesCount = useCallback(() => {
    return messageQueueRef.current.length;
  }, []);

  /**
   * Clear pending messages
   */
  const clearPendingMessages = useCallback(() => {
    messageQueueRef.current = [];
    updateNetworkStatus({ pendingMessages: 0 });
  }, [updateNetworkStatus]);

  /**
   * Retry pending messages
   */
  const retryPendingMessages = useCallback(() => {
    processMessageQueue();
  }, [processMessageQueue]);

  /**
   * Handle browser online event
   */
  const handleOnline = useCallback(() => {
    console.log('[WebSocket] Browser came online');
    updateNetworkStatus({
      isOnline: true,
      lastOnlineAt: Date.now(),
    });

    // Try to reconnect if we were previously connected
    if (connectionState === 'disconnected' || connectionState === 'error') {
      reconnectAttemptsRef.current = 0; // Reset retry count
      connect();
    }
  }, [connectionState, connect, updateNetworkStatus]);

  /**
   * Handle browser offline event
   */
  const handleOffline = useCallback(() => {
    console.log('[WebSocket] Browser went offline');
    updateNetworkStatus({
      isOnline: false,
      lastOfflineAt: Date.now(),
    });

    // Don't close the connection, let it timeout naturally
    // Messages will be queued until connection is restored
  }, [updateNetworkStatus]);

  /**
   * Setup network listeners on mount
   */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      updateNetworkStatus({
        isOnline: navigator.onLine,
      });
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      }
    };
  }, [handleOnline, handleOffline, updateNetworkStatus]);

  /**
   * Auto-connect on mount if enabled
   */
  useEffect(() => {
    if (config.autoConnect) {
      connect();
    }

    // Only disconnect on actual unmount, not StrictMode simulation
    // Use a ref to track if this is a real unmount
    const wsInstance = wsRef.current;
    return () => {
      // Close the specific instance that was created in this effect
      if (wsInstance && wsInstance.readyState !== WebSocket.CLOSED) {
        wsInstance.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic cleanup of stale messages
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      cleanStaleMessages();
    }, 60000); // Every minute

    return () => clearInterval(cleanupInterval);
  }, [cleanStaleMessages]);

  return {
    connectionState,
    connect,
    disconnect,
    send,
    lastMessage,
    error,
    networkStatus,
    getPendingMessagesCount,
    clearPendingMessages,
    retryPendingMessages,
  };
}

export default useWebSocket;
